import Vue from "vue";
import Vuex from "vuex";
// axios GET, POST, PUT 配置。
import stockAPI from "@/utility/api.js";
// 清除、啟用與管理 Cookie，token 驗證權限使用。
import Cookies from "js-cookie";
// 中英標題映射表。
import { termInChinese } from "@/utility/termInChinese.js";

// 個股頁面。
import { reportYear } from "@/store/reportYear";
import { reportRatioYear } from "@/store/reportRatioYear";

import chartAsset from "@/store/chartAsset";
import chartProfit from "@/store/chartProfit";

import { reportQuarter } from "@/store/report/quarter.js";
import { reportRatioQuarter } from "@/store/reportRatio/quarter.js";

// 選股頁面。
import { filterRate } from "@/store/filterRate";

import userCenter from "@/store/UserCenter";
import accountCenter from "@/store/AccountCenter";
import news from "@/store/News";
import survey from "@/store/Survey";
import statistics from "@/store/Statistics";
import newlyAdded from "@/store/newlyAdded";
import websiteDescriptions from "@/store/WebsiteDescriptions";

Vue.use(Vuex);

export default new Vuex.Store({
  strict: true,
  modules: {
    reportYear,
    reportQuarter,
    reportRatioYear,
    reportRatioQuarter,
    chartAsset,
    chartProfit,
    filterRate,
    userCenter,
    accountCenter,
    news,
    survey,
    statistics,
    newlyAdded,
    websiteDescriptions
  },
  state: {
    stockSymbol: "",
    companyName: "",
    companyInfoInChartTitle: {},
    currentTab: "report-year",
    layer: [{ column_name: "", id: 0, order: 0, parent: "", table_name: "" }],
    loggedInInfo: {},
    verifyNewAccount: {}, //for 取得通知信後新增密碼
    currentForgetFlow: "", //for 忘記帳號、密碼
    verifyAccount: {} //for 忘記密碼
  },
  getters: {
    // ------------------ 分頁共用搜尋、階層與登入資料 ------------------

    // 判斷是否取得有效 token，登入畫面。
    isLogin: state => !!state.loggedInInfo.api_token,
    // 輸出股票代號。
    outputOfStockSymbol: state => state.stockSymbol,
    // 輸出公司名稱。
    outputOfCompanyName: ({ companyName }) => companyName,
    companyInfoInChartTitle: state => {
      if (!state.companyInfoInChartTitle.level) {
        return { ...state.companyInfoInChartTitle, level: "-" };
      } else {
        return state.companyInfoInChartTitle;
      }
    },
    // 判斷位於何處頁面，使用者觸發搜尋按鈕時，輸出相對應 actions 方法。
    outputOfCurrentTab: ({ currentTab }) => {
      return `FETCH_${currentTab.toUpperCase().replace(/-/g, "_")}`;
    },
    // 取得所有標題階級。
    layerOfState: state => state.layer,

    // ------------------ 年表、財務比例分析＿季 ------------------

    // 共用各區 Period 期別標題與 labels 種類次標題。
    fieldsOfQuarter: () => labels => {
      return [
        {
          key: "subTitle",
          label: "種類",
          stickyColumn: true
        },
        ...labels.map(field => {
          return {
            key: field.quarter.toString(10),
            label: "合併",
            tdClass: "tdClass",
            // 保留原本型別 number 數值，純畫面字串化顯示。
            formatter: value => {
              return value.toLocaleString().replace(/-(.+)/g, "($1)");
            }
          };
        })
      ];
    },

    // ------------------ 選股複用格式轉化區 ------------------

    // 全區標題英翻中格式。
    map2Character: () => subTitle => termInChinese.get(subTitle),

    // 全區對應其數值轉換成百分比。
    number2Percentage: () => (current, field) =>
      parseFloat((current[field] * 100).toFixed(1)),

    // 判斷欄位 order 數值，設定相對應空白符格式。
    layer2Space: () => order => {
      return Array(Math.floor(order / 2))
        .fill("　")
        .join("");
    },

    // 取得年表、財務比例分析＿年(季)，共用各區欄位標題階層。
    layerOfTableArea: (state, { layerOfState, layer2Space }) => dataSets => {
      return dataSets.map(({ subTitle, ...pairs }) => {
        layerOfState.forEach(({ column_name, order }) => {
          if (termInChinese.get(column_name) === subTitle) {
            subTitle = layer2Space(order) + subTitle;
          }
        });
        return { subTitle, ...pairs };
      });
    },

    loggedInInfo(state) {
      return state.loggedInInfo;
    },

    //for 取得通知信後新增密碼
    initVerifyNewAccount() {
      return {
        password: "",
        password_confirmation: ""
      };
    },
    verifyNewAccount(state, getters) {
      return {
        ...getters.initVerifyNewAccount,
        ...state.verifyNewAccount
      };
    },

    //for 忘記帳號、密碼
    currentForgetFlow(state) {
      return state.currentForgetFlow;
    },
    initVerifyAccount() {
      return {
        password: "",
        password_confirmation: ""
      };
    },
    verifyAccount(state, getters) {
      return {
        ...getters.initVerifyAccount,
        ...state.verifyAccount
      };
    }
  },
  mutations: {
    // 儲存輸入股票代號。
    inputValueOfStockSymbol(state, tradeName) {
      state.stockSymbol = tradeName;
    },
    // 儲存公司名稱，顯示於搜尋列 prepend，因返回資料格式不同，需進行比對。
    storageCompanyName(state, res) {
      res.company_name
        ? (state.companyName = res.company_name)
        : (state.companyName = res.slice().shift().company_name);
    },
    companyInfoInChartTitle(state, res) {
      state.companyInfoInChartTitle = (({
        level,
        closing_price,
        net_value_per_share,
        EPS_of_recently_four_seasons,
        sum_of_EBIDTA,
        share_capital
      }) => ({
        level,
        closing_price,
        net_value_per_share:
          net_value_per_share === null ? "NA" : net_value_per_share.toFixed(2),
        EPS_of_recently_four_seasons: EPS_of_recently_four_seasons.toFixed(2),
        sum_of_EBIDTA: sum_of_EBIDTA.toFixed(2),
        // 加上千本位逗號
        share_capital: share_capital
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      }))(res);
    },
    // 儲存當下 CurrentTab 名稱，作為 dispatch 觸發動態 components 判斷機制。
    storageCurrentTab(state, tab) {
      state.currentTab = tab;
    },
    // 儲存共用各區欄位標題階層。
    storageLayer(state, res) {
      state.layer = res;
    },
    // 清除 token，使用者登出。
    logout() {
      location.reload();
      Cookies.remove("chuenStock");
      Cookies.remove("chuenStock_loggedInInfo");
    },
    loggedInInfo(state, res) {
      state.loggedInInfo = Object.assign({}, res);
      Cookies.set("chuenStock_loggedInInfo", res);
    },

    //for 取得通知信後新增密碼
    verifyNewAccountToken(state, token) {
      state.verifyNewAccount = {
        ...state.verifyNewAccount,
        token
      };
    },
    verifyNewAccountEmail(state, email) {
      state.verifyNewAccount = {
        ...state.verifyNewAccount,
        email
      };
    },
    verifyNewAccountNewPassword(state, password) {
      state.verifyNewAccount = {
        ...state.verifyNewAccount,
        password
      };
    },
    verifyNewAccountNewPasswordConfirmation(state, password_confirmation) {
      state.verifyNewAccount = {
        ...state.verifyNewAccount,
        password_confirmation
      };
    },

    //for 忘記帳號、密碼
    currentForgetFlow(state, type) {
      state.currentForgetFlow = type;
    },
    verifyAccountToken(state, token) {
      state.verifyAccount = {
        ...state.verifyAccount,
        token
      };
    },
    verifyAccountEmail(state, email) {
      state.verifyAccount = {
        ...state.verifyAccount,
        email
      };
    },
    verifyAccountNewPassword(state, password) {
      state.verifyAccount = {
        ...state.verifyAccount,
        password
      };
    },
    verifyAccountNewPasswordConfirmation(state, password_confirmation) {
      state.verifyAccount = {
        ...state.verifyAccount,
        password_confirmation
      };
    }
  },
  actions: {
    async FETCH_TOKEN({ commit }, loginForm) {
      const res = await stockAPI.POST("/api/login", loginForm);
      if (res) {
        Cookies.set("chuenStock", res.api_token);
        commit("loggedInInfo", res);
      }
      return Promise.resolve();
    },
    async FETCH_LAYER({ commit, getters }) {
      try {
        // 判斷欄位階層 API 是否已觸發，避免切換個股與選股多次請求。
        if (getters.layerOfState.slice().shift().order) return;
        const res = await stockAPI.GET("api/layer");
        // 進到 Stock 主頁時，routes beforeEnter 統一觸發請求機制。
        commit("storageLayer", res);
      } catch ({ message }) {
        console.error(message);
      }
    },
    // eslint-disable-next-line no-unused-vars
    register({ state }, applicationForm) {
      return stockAPI.POST("/api/register", applicationForm);
    },
    createNewPassword({ getters }) {
      return stockAPI.POST("/api/password/set", getters.verifyNewAccount);
    },
    // eslint-disable-next-line no-unused-vars
    sentForgetPasswordEmail({ state }, email) {
      return stockAPI.POST("/api/password/email", email);
    },
    resetPassword({ getters }) {
      return stockAPI.POST("/api/password/reset", getters.verifyAccount);
    },
    // eslint-disable-next-line no-unused-vars
    sentForgetAccountEmail({ state }, email) {
      return stockAPI.POST("/api/account/email", email);
    },
    // eslint-disable-next-line no-unused-vars
    fetchCompanys({ state }, value) {
      const data = {
        key_word: value
      };
      return stockAPI.POST("api/stock", data);
    }
  }
});
